import React, { useContext, useEffect, useMemo, useState } from 'react';
import ExcelDownloader from '../shared/ExcelDownloader';
import { AcquisitionExport } from '../../Config/config';
import Constants from '../../Constants';
import { Link, matchPath, useLocation, useParams } from 'react-router-dom';
import OverflowToolTip from '../shared/OverflowToolTip';
import  { routeNames } from '../../routeSegments';
import moment from 'moment';
import useModule from '../../hooks/useModule';
import RandomLogo from '../RandomLogo';
import CompanyLogo from '../CompanyLogo';
import DataTable from '../DataTables/DataTable';
import { UserContext } from '../../Contexts/UserContext';
import AllDataTable from '../DataTables/AllDataTable';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MonthPickerComponent from '../../Utils/MonthPickerComponent';

/* The above code is a React component written in JavaScript. It is a component called "Acquisitions"
that displays a table of acquisitions data. */
const AllAcquistions = ({ acquirers, acquirerName, isLoading}) => {
    const { acquirerId } = useParams();
    const location = useLocation();
    const [showXlDownload, setshowXlDownload] = useState(false);
    const { primaryMasterId } = useModule();
    const {isTrialExport,isStudent} = useContext(UserContext)
    const { monthPickerValue } = useContext(PeFilterContext);

    /* The `customStringSortMemo` constant is a memoized function that is used for sorting strings in a
    specific column of a data table. */
    const customStringSortMemo = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            const defaultVal = desc ? '--' : 'A';
            return (rowA?.values[columnId]?.name ?? defaultVal).localeCompare(rowB?.values[columnId]?.name ?? defaultVal);
        }
    }, []);

    /* The `dealDateSort` constant is a memoized function that is used for sorting the "Deal Date"
    column in a data table. */
    const dealDateSort = useMemo(() => {
        return (rowA, rowB, columnId, desc) => {
            return moment.utc(rowA?.values[columnId].date).diff(moment.utc(rowB?.values[columnId].date))
        }
    }, [])
   

    /**
     * The function adds commas to a number to improve readability.
     * @returns The function `addCommasToNumber` returns a string with commas added to the number.
     */
    const addCommasToNumber = (number) => {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

    /* The `const columns` is an array of objects that defines the columns for a data table in the
    Acquisitions component. Each object represents a column and contains properties such as `Header`
    (the column header text), `accessor` (the key to access the data for that column), and `Cell` (a
    custom component to render the cell content). */
    const columns = useMemo(() => [
        {
            Header: "Target",
            accessor: "target",
            Cell: ({ value }) => {
                return <div className='leading-[2rem] font-sans_book_body items-center justify-center w-full'style={{whiteSpace:"break-spaces"}}>
                    {value?.logo_path == "default.png" || value?.logo_path == "/default.png" || !value?.logo_path ?
                        <RandomLogo name={value.name} page="company page" />
                        :
                        <CompanyLogo name={value.name} page="company page" logo={value?.logo_path} />
                    }

                    <OverflowToolTip className='sm:w-[10rem] md:w-[15rem] lg:w-[20rem] lg:max-w-[20rem] 2xl:max-w-[30rem]' component={
                        <Link className='flex text-start lg:inline leading-[1.1rem] hover:text-[#BC8B20] w-full'
                            state={{ cameFrom: location.state?.prevPath }}
                            to={routeNames.maCompanyDealById(value?.id, value?.dealId)}>
                            {value.name ?? "--"}
                            {value?.agghide !== "" ? (
                                <span className="rounded-md bg-[#f3e8ff] px-2  py-[3px] ml-2 text-xs  font-sans_book_body text-[#3939ab]">
                                    {value?.agghide}
                                </span>
                            ) : null}
                            {value?.asset !== "" ? (
                                <span className="rounded-md bg-[#f3e8ff] px-2  py-[3px] ml-2 text-xs  font-sans_book_body text-[#3939ab]">
                                    {value?.asset}
                                </span>
                            ) : null}
                        </Link>
                    } />
                </div>

            },
            sortType: customStringSortMemo,
        },
      
        {
            Header: "Sector", 
            accessor: "sector",
            Cell:(cell)=>{
                return <span style={{whiteSpace:"break-spaces"}}>
                <OverflowToolTip className=' w-[10rem] lg:max-w-[20rem] 2xl:max-w-[30rem]' component={cell?.value} />
                </span>
            }
        },
        {
            id: "dealDate",
            Header: "Deal Date",
            accessor: "dealDate",

            Cell: (cell) => {
                return <Link state={{ cameFrom: location.state?.prevPath ?? "" }}
                    to={routeNames.maCompanyDealById(cell.value.id, cell?.value?.dealId)}>
                    {cell?.value?.date != '' ? moment(cell?.value?.date , 'MMM YYYY').format('MMM YYYY') : "--"}
                </Link>
            },
            sortType: dealDateSort
        },
        
        { 
            Header: "Deal Amount (US$ M)",
            accessor: "dealAmount",
            className: "px-0 text-right pr-[2.5rem] w-[12%]",
            headerspanClassName: "ml-auto",
            Cell: (cell) => {
                return addCommasToNumber(cell?.value)
            },
        },
        {
            Header: "Enterprise Value (INR Cr)",
            accessor: "enterpriseValue",
            className: "px-0 text-right pr-[2.5rem] w-[10%]",
            headerspanClassName: "ml-auto",
            Cell: (cell) => {
                return addCommasToNumber(cell?.value)
            },
        }

    ], []);

    /* The `rowData` constant is using the `useMemo` hook to memoize the creation of an array of
    objects. Each object represents a row of data for the DataTable component in the Acquisitions
    component. */
    const rowData = useMemo(() => {
        const rows = [];
        
        const startDate = new Date(monthPickerValue[0]);
        const endDate = new Date(monthPickerValue[1]);

        acquirers?.forEach((acquirer, i) => {
            const dealDateStr = acquirer?.deal_date;
            const dealDate = dealDateStr ? new Date(dealDateStr) : null;

            // Check if we need to filter by date
            const shouldInclude = (dealDate && dealDate >= startDate && dealDate <= endDate)
            if (shouldInclude) {
                rows.push({
                    target         : { name: acquirer?.target, dealId: acquirer?.deal_id, id: acquirer?.company_id, logo_path: acquirer?.icon_path, agghide: acquirer?.agghide, asset: acquirer?.asset },
                    dealDate       : { date: acquirer?.deal_date, dealId: acquirer?.deal_id, id: acquirer?.company_id },
                    sector         : acquirer?.sector,
                    dealAmount     : acquirer?.deal_amount> 0 ? acquirer?.deal_amount : "",
                    enterpriseValue: acquirer?.enterprise_value > 0 ? acquirer?.enterprise_value : "",
                })
            }
        });
        setshowXlDownload(rows.length !== 0)
        return rows;
    }, [acquirers, monthPickerValue])

    /* The `initialSort` constant is using the `useMemo` hook to memoize the creation of an array. This
    array is used to specify the initial sorting configuration for the data table in the
    Acquisitions component. */
    const initialSort = useMemo(() => [
        {
            id: "dealDate",
            desc: true
        }
    ], []);

    const totAmount = rowData?.reduce((acc, curr) => {
        return acc + parseFloat(curr?.dealAmount || 0);
    }, 0);


    return (
        <div className="py-4 bg-[#EDEDED]">
            <div className=" bg-white border sm:rounded-lg shadow-[0_2px_6px_0_rgba(0,0,0,0.08)]">
                <div className="pt-4 px-5 border-b border-b-[#F1F1F1] ">
                    <div className="flex justify-between mb-2">
                        <p className="text-[14pt] leading-[19pt] text-[#333333] lg:text-[17px] font-semibold mb-3 lg:leading-[22px]">Acquisitions</p>
                        <div className='pr-4 flex flex-row items-center gap-2'>
                            <p className='px-2 text-[#bc8b20]'>
                                No of Acquisitions:
                                <span className='font-semibold'>
                                    {" "}{rowData?.length};
                                </span> {" "}
                                Amount: 
                                <span className='font-semibold'>
                                    {" "}$ {totAmount.toFixed(2)} M
                                </span>
                            </p>
                            <MonthPickerComponent />
                            {(isTrialExport != 'false' && isStudent == 'false'  && showXlDownload) && <ExcelDownloader
                                url={AcquisitionExport}
                                fileName={Constants.exportNames.acquisitions(acquirerName)}
                                data={{
                                    "acquirer_id": [acquirerId],
                                    "type": primaryMasterId,
                                    "export_from": "detail",
                                    date_range: {
                                        from_date: moment(monthPickerValue[0]).format("YYYY-MM-DD"),
                                        to_date: moment(monthPickerValue[1]).format("YYYY-MM-DD"),
                                    }
                            }} />}
                        </div>
                    </div>
                </div>
                <div className="space-y-6">
                    <div className="bg-white pb-2 rounded-[6px]">
                        <AllDataTable
                            columns={columns}
                            data={rowData}
                            sortBy={initialSort}
                            showLoader={isLoading}
                        />
                    </div>
                </div>

            </div>
        </div>
    )
};

export default AllAcquistions;